import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, AlertCircle, Loader2 } from "lucide-react";
import { B2B } from "@/constants/testIds";
import { useT } from "@/i18n/LanguageContext";
import B2BFormFields from "@/components/site/b2b/B2BFormFields";
import B2BSuccessBlock from "@/components/site/b2b/B2BSuccessBlock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  full_name: "",
  company: "",
  email: "",
  phone: "",
  country: "",
  brand_of_interest: "",
  inquiry_type: "distribution",
  message: "",
};

// Pulls the first user-friendly error out of a FastAPI/Pydantic error body.
function extractError(err, fallback) {
  const msg =
    err?.response?.data?.detail?.[0]?.msg ||
    err?.response?.data?.detail ||
    fallback;
  return typeof msg === "string" ? msg : fallback;
}

export default function B2BInquiry() {
  const { t } = useT();
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState({ state: "idle", error: null });
  const [portfolioToken, setPortfolioToken] = useState(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "submitting", error: null });
    try {
      const payload = { ...form };
      if (!payload.brand_of_interest) delete payload.brand_of_interest;
      if (!payload.phone) delete payload.phone;
      const res = await axios.post(`${API}/inquiries`, payload);
      setPortfolioToken(res?.data?.portfolio_token || null);
      setStatus({ state: "success", error: null });
      setForm(initial);
    } catch (err) {
      setStatus({
        state: "error",
        error: extractError(err, t("b2b.errorFallback")),
      });
    }
  };

  const submitting = status.state === "submitting";

  return (
    <section
      id="b2b"
      data-testid={B2B.root}
      className="relative py-24 lg:py-36 bg-[#001F39] text-white overflow-hidden"
    >
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-25">
        <div className="absolute -left-40 -top-40 w-[520px] h-[520px]"
          style={{ background: "radial-gradient(circle, #2CBFCF 0%, transparent 60%)" }} />
        <div className="absolute -right-40 top-1/2 w-[520px] h-[520px]"
          style={{ background: "radial-gradient(circle, #FF6B6B 0%, transparent 60%)" }} />
      </div>
      <img
        aria-hidden
        src="/hupla-emblem.svg"
        alt=""
        className="pointer-events-none select-none absolute -right-40 top-1/2 -translate-y-1/2 w-[760px] max-w-none opacity-[0.14]"
        style={{ filter: "brightness(0) invert(1)" }}
      />

      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <p className="overline text-[#2CBFCF]">{t("b2b.overline")}</p>
          <h2 className="font-display font-black mt-6 leading-[0.9] text-[clamp(2.25rem,5vw,4.5rem)]">
            {t("b2b.titleA")}
            <br />
            {t("b2b.titleB")}
            <span className="font-editorial italic text-[#2CBFCF]"> {t("b2b.titleC")}</span>
            {t("b2b.titleDot")}
          </h2>
          <p className="mt-8 text-lg text-white/80 leading-relaxed max-w-lg">
            {t("b2b.subtitle")}
          </p>
          <ul className="mt-8 space-y-3 text-white/80">
            {[t("b2b.bullet1"), t("b2b.bullet2"), t("b2b.bullet3"), t("b2b.bullet4")].map((line) => (
              <li key={line} className="flex items-start gap-3">
                <span className="mt-1.5 w-2 h-2 rounded-full bg-[#2CBFCF] flex-shrink-0" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-7">
          <motion.form
            data-testid={B2B.form}
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative bg-white text-[#0A0A0A] rounded-[28px] p-8 lg:p-12 border border-white/10 shadow-2xl"
          >
            <B2BFormFields form={form} update={update} />

            {status.state === "success" && (
              <B2BSuccessBlock portfolioToken={portfolioToken} apiBase={API} />
            )}
            {status.state === "error" && (
              <div
                data-testid={B2B.error}
                className="mt-6 flex items-start gap-3 p-4 rounded-2xl bg-red-50 border border-red-200 text-red-900"
              >
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <p className="text-sm">{status.error}</p>
              </div>
            )}

            <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
              <p className="text-xs text-neutral-500 max-w-md">{t("b2b.privacy")}</p>
              <button
                data-testid={B2B.submit}
                type="submit"
                disabled={submitting}
                className="group inline-flex items-center gap-3 rounded-full bg-[#001F39] text-white pl-6 pr-2 py-2 font-medium hover:bg-[#128999] transition-all disabled:opacity-60"
              >
                {submitting ? t("b2b.sending") : t("b2b.submit")}
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#2CBFCF] text-black">
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </span>
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
